import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { OptimisationTestTypeEnum } from '@/utils/enums';
import { scrollBarStyles } from '@/utils/theme';

const Dialog = styled(StyledDialog)`
  width: 384px;
`;

const StyledButton = styled(Button)`
  height: 38px;
  padding: 0 16px;
  font-weight: 600;
  font-size: 14px;
  line-height: 24px;
  margin-right: 16px;
  svg {
    transform: rotate(180deg);
    margin-left: 12px;
  }
  &:disabled {
    background: ${({ theme }) => theme.colors.white};
  }
`;

const Footer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.grayscaleLight};
  margin: 0 -24px;
  padding: 24px 24px 0;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 24px -12px;
  padding: 0 12px;
  max-height: 262px;
  overflow-y: auto;
  ${scrollBarStyles};
`;

interface ListButtonProps {
  $active: boolean;
}

const ListButton = styled.button.attrs({ type: 'button' })<ListButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  height: 46px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};

  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
    ${Text} {
      color: ${({ theme }) => theme.colors.purple};
    }
  }

  ${({ theme, $active }) =>
    $active &&
    css`
      border-color: ${theme.colors.purple};
      ${Text} {
        color: ${theme.colors.purple};
      }
    `}
`;

interface Props {
  type: OptimisationTestTypeEnum;
  batches: number[];
}

const DeleteAllUnitsButton: FC<Props> = ({ type, batches }) => {
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [batch, setBatch] = useState<number | null>(null);

  const alerts = useAlerts();

  const { id } = useParams();

  const queryClient = useQueryClient();

  const onConfirm = async () => {
    setIsLoading(true);
    try {
      await api.delete(`/v1/optimisation-tests/${id}/units-group/${type}`, {
        data: {
          batch,
        },
      });

      queryClient.invalidateQueries(['optimisation_tests'], {
        exact: false,
      });

      setShowModal(false);
      setBatch(null);

      alerts.success('Tests successfully deleted');
    } catch (err) {
      alerts.error();
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <StyledButton
        onClick={() => setShowModal(true)}
        color="secondary"
        disabled={!batches.length}
        type="button"
      >
        Delete options
        <svg
          width="8"
          height="6"
          viewBox="0 0 8 6"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M4 0.5L8 5.5H0L4 0.5Z" fill="currentColor" />
        </svg>
      </StyledButton>
      <AnimatePresence>
        {showModal && (
          <Modal zIndex={50}>
            <Dialog
              initial={{ scale: 0.3 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.3 }}
            >
              <CloseButton onClick={() => setShowModal(false)} type="button">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M13 1L1 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M13 13L1 1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </CloseButton>
              <Text $type="heading2">Delete tabs</Text>
              <List>
                <ListButton
                  $active={batch === null}
                  onClick={() => setBatch(null)}
                >
                  <Text as="span" $type="captionSemibold" $color="black">
                    All
                  </Text>
                </ListButton>
                {batches.map((t) => (
                  <ListButton
                    key={t}
                    $active={batch === t}
                    onClick={() => setBatch(t)}
                  >
                    <Text as="span" $type="captionSemibold" $color="black">
                      T{t}
                    </Text>
                  </ListButton>
                ))}
              </List>
              <Footer>
                <Button
                  type="button"
                  onClick={onConfirm}
                  loading={isLoading}
                  disabled={isLoading}
                >
                  Confirm
                </Button>
                <Button
                  onClick={() => setShowModal(false)}
                  color="secondary"
                  type="button"
                >
                  Cancel
                </Button>
              </Footer>
            </Dialog>
          </Modal>
        )}
      </AnimatePresence>
    </>
  );
};

export default DeleteAllUnitsButton;
